import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon } from '@persona-id/design-tokens/icons';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Pagination from 'dashboard/components/Pagination';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import TableControls, { FilterConfig } from 'dashboard/components-lib/TableControls';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Environment } from 'dashboard/models/environment';
import {
  BuilderPreviewMap,
  InquiryTemplate,
  InquiryTemplateBuilder,
  InquiryTemplateFilterOption,
  InquiryTemplateStatus,
} from 'dashboard/models/inquiry-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import { TemplateFilterOption, TemplateListPartial } from 'dashboard/models/template';
import {
  initialInquiryTemplatesFilterState,
  InquiryTemplatesFilterParams,
} from 'dashboard/redux/slices/inquiry-templates';
import CreateInquiryTemplateModal from 'dashboard/views/DashboardInquiryTemplatesView/CreateInquiryTemplateModal';
import InquiryTemplatesTable from 'dashboard/views/DashboardInquiryTemplatesView/InquiryTemplatesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import useToggle from 'lib/hooks/useToggle';
import { PaginationParams } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';

const STATUS_FILTER_OPTIONS: {
  label: string;
  value: InquiryTemplateStatus;
}[] = [
  {
    label: 'Active',
    value: InquiryTemplateStatus.Active,
  },
  {
    label: 'Inactive',
    value: InquiryTemplateStatus.Inactive,
  },
];

const TableTitleContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-column-gap: ${Spacing[4]};
`;

const modalStyles: ReactModal.Styles = {
  content: {
    overflowX: 'hidden',
  },
};

interface DashboardInquiryTemplatesViewProps {
  inquiryTemplates: Array<TemplateListPartial | InquiryTemplate>;
  legacyTemplateFilters: Array<TemplateFilterOption>;
  inquiryTemplateFilters: Array<InquiryTemplateFilterOption>;
  inquiryTemplateBuilders: InquiryTemplateBuilder[];
  builderPreviews: BuilderPreviewMap;
  isLoading: boolean;
  isCloneLoading: boolean;
  filters: InquiryTemplatesFilterParams;
  sortParams: SortParams;
  paginationParams: PaginationParams;
  pageSize: number;
  currentEnvironmentType: Environment['type'];
  handleStatusFilterUpdate: (status: InquiryTemplateStatus[]) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleOmniSearch: (omniQuery: string) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  handleLoadTemplateBuilderPreview: (builderName: string) => Promise<void>;
  handleCreateInquiryTemplate: (builderName: string) => Promise<void>;
  handlePreviewInquiry: (
    inquiryTemplateId: string,
    inquiryTemplateVersionId: string,
    environmentName?: string,
  ) => void;
  handlePreviewLegacyInquiry: (templateId: string) => void;
  handleActivateInquiryTemplate: (inquiryTemplateId: string) => void;
  handleDeactivateInquiryTemplate: (inquiryTemplateId: string) => void;
  handleCloneInquiryTemplate: (inquiryTemplateId: string, name: string | undefined) => void;
  dynamicFlowTemplatesOnly: boolean;
}

const DashboardInquiryTemplatesView = ({
  inquiryTemplates,
  legacyTemplateFilters,
  inquiryTemplateFilters,
  inquiryTemplateBuilders,
  builderPreviews,
  isLoading,
  isCloneLoading,
  filters,
  sortParams,
  paginationParams,
  pageSize,
  currentEnvironmentType,
  handleStatusFilterUpdate,
  handlePageLinkClick,
  handlePageSizeClick,
  handleOmniSearch,
  handleSortUpdate,
  handleLoadTemplateBuilderPreview,
  handleCreateInquiryTemplate,
  handlePreviewInquiry,
  handlePreviewLegacyInquiry,
  handleActivateInquiryTemplate,
  handleDeactivateInquiryTemplate,
  handleCloneInquiryTemplate,
  dynamicFlowTemplatesOnly,
}: DashboardInquiryTemplatesViewProps) => {
  const { omni, status } = filters;
  // A filter is set by default so we check for equality rather than emptiness
  const hasUsedFilters = !isEqual(filters, initialInquiryTemplatesFilterState);
  const statusFilterLabelTextMapper = useLabelTextMapper(STATUS_FILTER_OPTIONS, 'statuses', {
    emptyIsAll: true,
  });

  const [
    showCreateInquiryTemplatesModal,
    { enable: openCreateInquiryTemplatesModal, disable: hideCreateInquiryTemplatesModal },
  ] = useToggle();

  const handleCloseModal = useCallback(() => {
    if (!isLoading) {
      hideCreateInquiryTemplatesModal();
    }
  }, [isLoading, hideCreateInquiryTemplatesModal]);

  const isITEInternal = useIsITEInternal();
  const isInquiriesFree = useProductFeature(ProductFeatureName.InquiriesFree);
  const isInquiriesBasic = useProductFeature(ProductFeatureName.InquiriesBasic);
  const isInquiriesEnhanced = useProductFeature(ProductFeatureName.InquiriesEnhanced);

  const hasTemplateCreatePermission = usePermission(Permission.InquiryTemplateCreate, {
    global: true,
  });

  const canCreateTemplates =
    (isInquiriesFree || isInquiriesBasic || isInquiriesEnhanced || isITEInternal) &&
    hasTemplateCreatePermission;

  const location = useLocation();
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const openCreateInquiryTemplateModalParam = queryParams.get('openCreateInquiryTemplateModal');

    if (openCreateInquiryTemplateModalParam === 'true' && canCreateTemplates) {
      openCreateInquiryTemplatesModal();
    }
  }, [location, canCreateTemplates]);

  const filterConfig: FilterConfig[] = [
    {
      id: 'status',
      name: 'Status',
      type: 'multiselect-legacy',
      labelTextMapper: statusFilterLabelTextMapper,
      selectOptionValues: STATUS_FILTER_OPTIONS,
      onChange: handleStatusFilterUpdate,
      value: status ?? [],
    },
  ];
  // Cloning action's loading visuals are different than other actions, it takes longer
  // and after the successful response, it will transition into the draft ITV's editor screen
  // where it has a full screen LoadingIndicator, below ensure smooth visual transition.
  if (isCloneLoading) {
    return (
      <Container>
        <LoadingIndicator fullscreen />
      </Container>
    );
  }
  return (
    <Container>
      <Header title='Inquiry Templates'>
        {canCreateTemplates && (
          <HStack>
            <Button
              variant='primary'
              id='inquiry-templates-view__create-inquiry-template-button'
              name='create-inquiry-template.button'
              prefixIcon={<PlusIcon />}
              onClick={openCreateInquiryTemplatesModal}
            >
              Create template
            </Button>
          </HStack>
        )}
      </Header>
      <Section>
        <Card>
          <Card.Body>
            <TableTitleContainer>
              <Card.Title
                id='inquiry-templates-view__card-title'
                data-test='inquiry-templates-view__card-title'
              >
                Inquiry templates
              </Card.Title>
            </TableTitleContainer>
            <TableControls
              searchProps={{
                id: 'inquiries-table__search',
                handleInput: handleOmniSearch,
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                value: omni,
                placeholder: 'Search by inquiry template ID, or name',
              }}
              filters={filterConfig}
            />
            <InquiryTemplatesTable
              inquiryTemplates={inquiryTemplates}
              legacyTemplateFilters={legacyTemplateFilters}
              inquiryTemplateFilters={inquiryTemplateFilters}
              hasUsedFilters={hasUsedFilters}
              isLoading={isLoading}
              canSort={dynamicFlowTemplatesOnly && isEmpty(omni)}
              sortParams={sortParams}
              currentEnvironmentType={currentEnvironmentType}
              handleSortUpdate={handleSortUpdate}
              handlePreviewInquiry={handlePreviewInquiry}
              handlePreviewLegacyInquiry={handlePreviewLegacyInquiry}
              handleActivateInquiryTemplate={handleActivateInquiryTemplate}
              handleDeactivateInquiryTemplate={handleDeactivateInquiryTemplate}
              handleCloneInquiryTemplate={handleCloneInquiryTemplate}
            />
            {dynamicFlowTemplatesOnly && (
              <Pagination
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </Section>
      <ModalWrapper
        shouldCloseOnEsc={true}
        styleOverride={modalStyles}
        isOpen={showCreateInquiryTemplatesModal}
        handleCloseModal={handleCloseModal}
      >
        <CreateInquiryTemplateModal
          inquiryTemplateBuilders={inquiryTemplateBuilders}
          builderPreviews={builderPreviews}
          handleCloseModal={hideCreateInquiryTemplatesModal}
          handleCreateInquiryTemplate={handleCreateInquiryTemplate}
          handleLoadTemplateBuilderPreview={handleLoadTemplateBuilderPreview}
        />
      </ModalWrapper>
    </Container>
  );
};

export default DashboardInquiryTemplatesView;
